import { Box, Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useServerProductValidation } from '../hooks/useServerProductValidation';
import { arePricesDirty, toPricePayload } from '../hooks/useUpsertPriceForm';
import { useUpsertProductForm } from '../hooks/useUpsertProductForm';
import type { UpsertProductDto } from '../types/ProductDto';
import { UpsertProductForm } from './UpsertProductForm';

interface NewProductModalProps {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
}

export function NewProductModal({
	isOpened,
	close,
	triggerListUpdate,
}: NewProductModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertProductForm();

	const newProductMutation = mutate<UpsertProductDto>(
		`${organisation}/product/new`,
		{
			...inputs,
			prices: arePricesDirty(form, 'prices.')
				? toPricePayload(inputs.prices)
				: undefined,
		},
	);

	useServerProductValidation(newProductMutation, form);
    
	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		triggerListUpdate();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'New Product'} />
						<Code>
							Branch stock levels and barcodes can be modified after adding the
							product.
						</Code>
					</>
				}
				withCloseButton
				size={'xl'}
				centered
			>
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Product Added!'}
					body={'A new product has been added. Click Ok to continue...'}
				/>
				<Box pos={'relative'}>
					<LoadingOverlay visible={newProductMutation.isLoading} />
					<Space h={6} />
					<form
						onSubmit={form.onSubmit(async () => {
							await newProductMutation.mutateAsync().then((value) => {
								if (value.isValid) {
									openSuccessModal();
								}
							});
						})}
					>
						<UpsertProductForm form={form} />
						<FormButtons close={resetAndClose} />
					</form>
				</Box>
			</Modal>
		</>
	);
}
